import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ColorAdvice2, Spacing8, Spacing16, Spacing32, Spacing48 } from 'autogen/design-tokens/tokens';
import { ReadLess, ReadMore } from 'autogen/translation-keys/trans-website-shared-buttons';
import { Body, Typography } from 'components/generic/typography';
import { ReactReadMoreMaxLines } from 'components/react-readmore';
import { toHtmlContent } from 'modules/helpers/strings';
import { rem } from 'modules/helpers/style';
import { breakpoint, sizes } from 'variables';
import AdvisorHeader from './AdvisorHeader';
import { useRegime } from './Regime';
import SoldOutReminder from './SoldOutReminder';

/* #region  styles */
const Container = styled.div`
    display: flex;
    flex-direction: column;

    ${breakpoint.up(sizes.md)} {
        width: 50%;
        margin-right: ${Spacing32};
    }

    ${breakpoint.up(sizes.lg)} {
        width: 50%;
        border-right: 1px solid ${ColorAdvice2};
        margin-right: ${Spacing48};
        padding-right: ${Spacing48};
    }

    ${breakpoint.up(sizes.xl)} {
        width: 42%;
    }
`;

const StyledAdvisorHeader = styled(AdvisorHeader)`
    margin-bottom: ${Spacing16};
`;

const IntroductionContainer = styled.div``;

const TextContainer = styled.div`
    display: none;

    ${breakpoint.up(sizes.sm)} {
        display: grid;
        margin-top: ${Spacing16};
        gap: ${Spacing16};
    }
`;

const ProductDescription = styled.div``;

const Headline = styled.div`
    display: flex;
    align-items: center;
    gap: ${Spacing8};
`;

const StyledBody = styled(Typography.Body)<{ isSelected: boolean }>`
    opacity: ${({ isSelected }) => (isSelected ? 1 : 0.4)};
`;

const StyledReadMoreMaxLines = styled(ReactReadMoreMaxLines)`
    font-size: ${rem(14)};
`;

/* #endregion */

export default function RegimeInformation({ information }) {
    const { productEntries, isSelected } = useRegime();
    const { t } = useTranslation();

    return (
        <Container>
            <StyledAdvisorHeader advisor={information.advisor} />
            {information.introText ? (
                <IntroductionContainer>
                    <StyledReadMoreMaxLines
                        lines={{ xs: 3, sm: 3, md: 3, lg: 3, xl: 3, xxl: 3, xxxl: 3 }}
                        lineHeight={21}
                        fontSize={14}
                        readMore={t(ReadMore)}
                        readLess={t(ReadLess)}
                    >
                        {toHtmlContent(information.introText)}
                        <Body size="S" />
                    </StyledReadMoreMaxLines>
                </IntroductionContainer>
            ) : null}

            <TextContainer>
                {productEntries.map((product) => {
                    if (
                        !product.sectionInfo.body ||
                        !product.sectionInfo.headline ||
                        !product.sectionInfo.headline.length
                    )
                        return null;

                    return (
                        <ProductDescription key={product.product.id}>
                            <Headline>
                                <StyledBody
                                    size="M"
                                    strong
                                    forwardedAs="div"
                                    isSelected={!product.stockAvailable || isSelected(product.product.id)}
                                >
                                    {product.sectionInfo.headline}
                                </StyledBody>
                                {!product.stockAvailable && <SoldOutReminder product={product} />}
                            </Headline>
                            <StyledBody
                                forwardedAs="div"
                                isSelected={isSelected(product.product.id) && product.stockAvailable}
                            >
                                {product.sectionInfo.body}
                            </StyledBody>
                        </ProductDescription>
                    );
                })}
            </TextContainer>
        </Container>
    );
}
